/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { Modal, ModalHeader, ModalBody } from "baseui/modal";
import { BiArrowBack } from "react-icons/bi";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { AxiosContext } from "../Utils/AuthContext";
import { EventLog } from "Api";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../DesignSystem/TooltipStyles";

export interface SystemLoggerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SystemLoggerModal: React.FC<SystemLoggerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { currentTheme } = useDexterTheme();
  const axiosContext = React.useContext(AxiosContext);

  const [allLogs, setAllLogs] = React.useState<EventLog[]>([]);
  const [filteredLogs, setFilteredLogs] = React.useState<EventLog[]>([]);
  const [levelFilter, setLevelFilter] = React.useState<string>("all");
  const [componentFilter, setComponentFilter] = React.useState<string>("all");
  const [selectedLog, setSelectedLog] = React.useState<EventLog | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [, setRefreshKey] = React.useState<number>(0);
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "desc"
  );

  // Update relative timestamps every minute while modal is open
  // Using requestAnimationFrame for better reliability in containerized environments
  React.useEffect(() => {
    if (!isOpen) return;

    const intervalId = setInterval(() => {
      setRefreshKey((prev) => prev + 1);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [isOpen]);

  const fetchLogs = React.useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await axiosContext.get("/getAllEventLogs");

      // Safety check: ensure we got an array (not HTML or unexpected format)
      if (!Array.isArray(response.data)) {
        console.error(
          "Unexpected response format - expected array, got:",
          typeof response.data
        );
        setAllLogs([]);
        setFilteredLogs([]);
        setIsLoading(false);
        return;
      }

      setAllLogs(response.data);
      setFilteredLogs(response.data);
      // Trigger timestamp recalculation when new logs are loaded
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to fetch event logs:", error);
      setAllLogs([]);
      setFilteredLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [axiosContext]);

  // Fetch logs from backend when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchLogs();
      // Reset refresh key when modal opens
      setRefreshKey(0);
    }
  }, [isOpen, fetchLogs]);

  const filterLogs = React.useCallback(
    (level: string, component: string): void => {
      if (!Array.isArray(allLogs)) {
        setFilteredLogs([]);
        return;
      }
      const filtered = allLogs.filter((log) => {
        const levelMatch = level === "all" || log.level === level;
        const componentMatch =
          component === "all" || log.component === component;
        return levelMatch && componentMatch;
      });
      setFilteredLogs(filtered);
      // Trigger timestamp recalculation on filter change
      setRefreshKey((prev) => prev + 1);
    },
    [allLogs]
  );

  const handleLevelFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const newLevel = e.target.value;
    setLevelFilter(newLevel);
    filterLogs(newLevel, componentFilter);
  };

  const handleComponentFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const newComponent = e.target.value;
    setComponentFilter(newComponent);
    filterLogs(levelFilter, newComponent);
  };

  const handleColumnSort = (column: string): void => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column, default to descending
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const sortedLogs = React.useMemo(() => {
    if (!sortColumn) return filteredLogs;

    const sorted = [...filteredLogs].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "level":
          // Sort by level priority: ERROR > WARNING > INFORMATION
          const levelPriority: { [key: string]: number } = {
            ERROR: 3,
            WARNING: 2,
            INFORMATION: 1,
          };
          aValue = levelPriority[a.level] || 0;
          bValue = levelPriority[b.level] || 0;
          break;
        case "component":
          aValue = a.component.toLowerCase();
          bValue = b.component.toLowerCase();
          break;
        case "message":
          aValue = a.message.toLowerCase();
          bValue = b.message.toLowerCase();
          break;
        case "timestamp":
          aValue = new Date(a.timeStamp).getTime();
          bValue = new Date(b.timeStamp).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredLogs, sortColumn, sortDirection]);

  const handleExportLogs = (): void => {
    if (!Array.isArray(filteredLogs) || filteredLogs.length === 0) {
      return;
    }

    const csvContent = [
      ["Level", "Component", "Message", "Timestamp"],
      ...filteredLogs.map((log) => [
        log.level,
        log.component,
        log.message.replace(/"/g, '""'),
        formatFullTimestamp(new Date(log.timeStamp)),
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `event-logs-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getLevelBadge = (level: string): JSX.Element => {
    const configs: {
      [key: string]: { text: string };
    } = {
      ERROR: { text: "Error" },
      WARNING: { text: "Warning" },
      INFORMATION: { text: "Info" },
    };
    const config = configs[level] || configs.INFORMATION;
    return (
      <span
        style={{
          color: currentTheme.colors.text,
          fontSize: "13px",
          fontWeight: 500,
        }}
      >
        {config.text}
      </span>
    );
  };

  // Format timestamp with dependency on refreshKey to force recalculation
  const formatTimestamp = React.useCallback((date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  }, []);

  const formatFullTimestamp = (date: Date): string => {
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const truncateMessage = (message: string, maxLength = 80): string => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + "...";
  };

  const capitalizeComponent = (component: string): string => {
    return component
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const getSortIndicator = (column: string): string => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const getColumnHeaderStyle = (column: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: `${currentTheme.spacing.md} ${currentTheme.spacing.lg}`,
      textAlign: "left",
      fontWeight: 600,
      color: currentTheme.colors.text,
      fontSize: "13px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      background: currentTheme.colors.tableBackgroundColor,
      cursor: "pointer",
      userSelect: "none",
      transition: "background 0.2s, opacity 0.2s",
    };

    if (sortColumn === column) {
      baseStyle.opacity = 1;
    }

    return baseStyle;
  };

  // Get unique components from all logs for the filter dropdown
  const uniqueComponents = React.useMemo(() => {
    if (!Array.isArray(allLogs)) {
      return [];
    }
    const components = new Set(allLogs.map((log) => log.component));
    return Array.from(components).sort();
  }, [allLogs]);

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      closeable
      animate
      autoFocus
      overrides={{
        Dialog: {
          style: {
            backgroundColor: currentTheme.colors.pageBackgroundColor,
            width: "80vw",
            maxWidth: "1200px",
            height: "80vh",
            display: "flex",
            flexDirection: "column",
          },
        },
        Close: {
          style: {
            color: currentTheme.colors.text,
          },
        },
      }}
    >
      <ModalHeader
        style={{
          color: currentTheme.colors.fileModalHeaderTextColor,
          fontFamily: currentTheme.fonts.primary,
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        {selectedLog && (
          <button
            onClick={() => setSelectedLog(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              color: currentTheme.colors.text,
              display: "flex",
              alignItems: "center",
            }}
          >
            <BiArrowBack size={20} />
          </button>
        )}
        <span>{selectedLog ? "Event Details" : "Event Log"}</span>
      </ModalHeader>
      <ModalBody
        style={{
          padding: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {!selectedLog ? (
          <>
            {/* Action Bar */}
            <div
              style={{
                padding: `${currentTheme.spacing.md} ${currentTheme.spacing.lg}`,
                borderBottom: `1px solid ${currentTheme.colors.searchMainColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                background: currentTheme.colors.pageBackgroundColor,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: currentTheme.fontSizes.small,
                    color: currentTheme.colors.secondaryTextColor,
                  }}
                >
                  Filter by level:
                </span>
                <select
                  value={levelFilter}
                  onChange={handleLevelFilterChange}
                  style={{
                    padding: "6px 32px 6px 10px",
                    border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
                    borderRadius: "6px",
                    fontSize: currentTheme.fontSizes.small,
                    background: currentTheme.colors.backgroundTertiary,
                    color: currentTheme.colors.text,
                    cursor: "pointer",
                    fontFamily: currentTheme.fonts.primary,
                  }}
                >
                  <option value="all">All levels</option>
                  <option value="INFORMATION">Information</option>
                  <option value="WARNING">Warning</option>
                  <option value="ERROR">Error</option>
                </select>
                <span
                  style={{
                    fontSize: currentTheme.fontSizes.small,
                    color: currentTheme.colors.secondaryTextColor,
                    marginLeft: "12px",
                  }}
                >
                  Filter by component:
                </span>
                <select
                  value={componentFilter}
                  onChange={handleComponentFilterChange}
                  style={{
                    padding: "6px 32px 6px 10px",
                    border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
                    borderRadius: "6px",
                    fontSize: currentTheme.fontSizes.small,
                    background: currentTheme.colors.backgroundTertiary,
                    color: currentTheme.colors.text,
                    cursor: "pointer",
                    fontFamily: currentTheme.fonts.primary,
                  }}
                >
                  <option value="all">All components</option>
                  {uniqueComponents.map((component) => (
                    <option key={component} value={component}>
                      {capitalizeComponent(component)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <StatefulTooltip
                  placement={PLACEMENT.top}
                  content={"Export (CSV)"}
                  overrides={getTooltipOverrides(currentTheme)}
                >
                  <button
                    onClick={handleExportLogs}
                    style={{
                      width: "32px",
                      height: "32px",
                      border: "none",
                      borderRadius: "6px",
                      background: currentTheme.colors.backgroundTertiary,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                      fontSize: "16px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `rgba(8, 129, 153, 0.1)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        currentTheme.colors.backgroundTertiary;
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 1V9M7 9L4 6M7 9L10 6M1 13H13"
                        stroke={currentTheme.colors.text}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </StatefulTooltip>
              </div>
            </div>

            {/* Event Count Badge */}
            <div
              style={{
                padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.lg}`,
                background: currentTheme.colors.pageBackgroundColor,
                borderBottom: `1px solid ${currentTheme.colors.searchMainColor}`,
              }}
            >
              <span
                style={{
                  fontSize: currentTheme.fontSizes.small,
                  color: currentTheme.colors.secondaryTextColor,
                }}
              >
                Showing{" "}
                <span
                  style={{ fontWeight: 500, color: currentTheme.colors.text }}
                >
                  {filteredLogs.length}
                </span>{" "}
                of{" "}
                <span
                  style={{ fontWeight: 500, color: currentTheme.colors.text }}
                >
                  {allLogs.length}
                </span>{" "}
                events
              </span>
            </div>

            {/* Log Table */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: currentTheme.spacing.xl,
                    color: currentTheme.colors.secondaryTextColor,
                  }}
                >
                  Loading logs...
                </div>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: currentTheme.fontSizes.medium,
                    fontFamily: currentTheme.fonts.primary,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: currentTheme.colors.tableBackgroundColor,
                        borderBottom: `2px solid ${currentTheme.colors.searchMainColor}`,
                      }}
                    >
                      <th
                        onClick={() => handleColumnSort("level")}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.backgroundTertiary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.tableBackgroundColor;
                        }}
                        style={{
                          ...getColumnHeaderStyle("level"),
                          borderRight: `1px solid ${currentTheme.colors.searchMainColor}`,
                          width: "100px",
                        }}
                      >
                        Level{getSortIndicator("level")}
                      </th>
                      <th
                        onClick={() => handleColumnSort("component")}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.backgroundTertiary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.tableBackgroundColor;
                        }}
                        style={{
                          ...getColumnHeaderStyle("component"),
                          borderRight: `1px solid ${currentTheme.colors.searchMainColor}`,
                          width: "150px",
                        }}
                      >
                        Component{getSortIndicator("component")}
                      </th>
                      <th
                        onClick={() => handleColumnSort("message")}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.backgroundTertiary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.tableBackgroundColor;
                        }}
                        style={{
                          ...getColumnHeaderStyle("message"),
                          borderRight: `1px solid ${currentTheme.colors.searchMainColor}`,
                        }}
                      >
                        Message{getSortIndicator("message")}
                      </th>
                      <th
                        onClick={() => handleColumnSort("timestamp")}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.backgroundTertiary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.tableBackgroundColor;
                        }}
                        style={{
                          ...getColumnHeaderStyle("timestamp"),
                          width: "160px",
                        }}
                      >
                        Timestamp{getSortIndicator("timestamp")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLogs.map((log, index) => (
                      <tr
                        key={index}
                        onClick={() => setSelectedLog(log)}
                        style={{
                          background: currentTheme.colors.tableBackgroundColor,
                          borderBottom: `1px solid ${currentTheme.colors.searchMainColor}`,
                          cursor: "pointer",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.backgroundTertiary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            currentTheme.colors.tableBackgroundColor;
                        }}
                      >
                        <td
                          style={{
                            padding: currentTheme.spacing.lg,
                            verticalAlign: "middle",
                            borderRight: `1px solid ${currentTheme.colors.searchMainColor}`,
                            borderTop: `1px solid ${currentTheme.colors.searchMainColor}`,
                          }}
                        >
                          {getLevelBadge(log.level)}
                        </td>
                        <td
                          style={{
                            padding: currentTheme.spacing.lg,
                            verticalAlign: "middle",
                            borderRight: `1px solid ${currentTheme.colors.searchMainColor}`,
                            borderTop: `1px solid ${currentTheme.colors.searchMainColor}`,
                            color: currentTheme.colors.text,
                            fontWeight: 500,
                          }}
                        >
                          {capitalizeComponent(log.component)}
                        </td>
                        <td
                          style={{
                            padding: currentTheme.spacing.lg,
                            verticalAlign: "middle",
                            borderRight: `1px solid ${currentTheme.colors.searchMainColor}`,
                            borderTop: `1px solid ${currentTheme.colors.searchMainColor}`,
                            color: currentTheme.colors.text,
                          }}
                        >
                          {truncateMessage(log.message)}
                        </td>
                        <td
                          style={{
                            padding: currentTheme.spacing.lg,
                            verticalAlign: "middle",
                            borderTop: `1px solid ${currentTheme.colors.searchMainColor}`,
                            color: currentTheme.colors.secondaryTextColor,
                            fontSize: currentTheme.fontSizes.small,
                          }}
                        >
                          {formatTimestamp(new Date(log.timeStamp))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          // Detail View
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: currentTheme.spacing.lg,
            }}
          >
            <div
              style={{
                background: currentTheme.colors.backgroundTertiary,
                borderRadius: "8px",
                padding: currentTheme.spacing.md,
                marginBottom: currentTheme.spacing.md,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: currentTheme.spacing.md,
                  marginBottom: currentTheme.spacing.md,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: currentTheme.colors.secondaryTextColor,
                      marginBottom: "4px",
                    }}
                  >
                    Level
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: 500 }}>
                    {getLevelBadge(selectedLog.level)}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: currentTheme.colors.secondaryTextColor,
                      marginBottom: "4px",
                    }}
                  >
                    Component
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: currentTheme.colors.text,
                    }}
                  >
                    {capitalizeComponent(selectedLog.component)}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: currentTheme.colors.secondaryTextColor,
                      marginBottom: "4px",
                    }}
                  >
                    Timestamp
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: currentTheme.colors.text,
                    }}
                  >
                    {formatFullTimestamp(new Date(selectedLog.timeStamp))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: currentTheme.fontSizes.small,
                  fontWeight: 500,
                  color: currentTheme.colors.text,
                  marginBottom: "8px",
                }}
              >
                Full Message
              </div>
              <div
                style={{
                  background: currentTheme.colors.backgroundTertiary,
                  borderRadius: "8px",
                  padding: currentTheme.spacing.md,
                  fontSize: "14px",
                  lineHeight: "1.6",
                  color: currentTheme.colors.text,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "'Courier New', monospace",
                }}
              >
                {selectedLog.message}
              </div>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};
